import { lazy, Suspense } from "react";
import { Link, matchPath, Outlet, Route, Routes, useLocation } from "react-router";
import { TerminalContent } from "../App";
import { AppLayout } from "../components/AppLayout";
import { isMerkleV4 } from "../config/trade";
import { DEX_TOKEN } from "../config/dex-token";
import { PageNavigation } from "../components/PageNavigation";
import { launchpadPagesEnabled } from "../config/navigation";
import { canAccessLiquidity } from "../config/liquidity-access";
import { bridgeReviewEnabled } from "../config/bridge";
import { useDemoTerminalModel } from "../demo/use-demo-model";
import { NotFoundPage } from "../pages/NotFoundPage";
import { XOAuthCallbackPage } from "../features/social-tasks/pages/XOAuthCallbackPage";
import { useTerminalAppModel } from "../state/app-model";
import { LiveRuntimeProvider } from "../state/live-runtime";
import { useLaunchVerification } from "../features/launchpad/state/use-launch-verification";
import { getPresentationPolicy } from "../state/presentation-policy";
import type { TerminalAppModel } from "../state/ui-types";
import { useWalletNotifications } from "../state/use-wallet-notifications";
import { PresentationBoundary, RecoveryNotice, reportPresentationFailure } from "../components/PresentationBoundary";

const TasksPage = lazy(() => import("../pages/TasksPage")
  .then(module => ({ default: module.TasksPage }))
  .catch(() => {
    reportPresentationFailure("tasks", "CHUNK_LOAD_FAILURE");
    return { default: TasksLoadError };
  }));

const TradePage = lazy(() => import("../pages/TradePage")
  .then(module => ({ default: module.TradePage }))
  .catch(() => {
    reportPresentationFailure("trade", "CHUNK_LOAD_FAILURE");
    return { default: TradeLoadError };
  }));

function TradeLoadError() {
  return <RecoveryNotice title="The Grove Exchange couldn’t load" reload>
    <Link to="/">Back to terminal</Link>
  </RecoveryNotice>;
}

const ExplorePage = lazy(() => import("../features/explore/ExplorePage")
  .then(module => ({ default: module.ExplorePage }))
  .catch(() => {
    reportPresentationFailure("explore", "CHUNK_LOAD_FAILURE");
    return { default: ExploreLoadError };
  }));

const TokenPage = lazy(() => import("../features/token/TokenPage")
  .then(module => ({ default: module.TokenPage }))
  .catch(() => {
    reportPresentationFailure("token", "CHUNK_LOAD_FAILURE");
    return { default: TokenLoadError };
  }));

function TokenLoadError() {
  return <RecoveryNotice title="Token detail couldn’t load" reload>
    <Link to="/explore">Back to Explore</Link>
  </RecoveryNotice>;
}

function ExploreLoadError() {
  return <RecoveryNotice title="Explore couldn’t load" reload>
    <Link to="/">Back to terminal</Link>
  </RecoveryNotice>;
}

const CreatePage = lazy(() => import("../pages/CreatePage")
  .then(module => ({ default: module.CreatePage }))
  .catch(() => {
    reportPresentationFailure("create", "CHUNK_LOAD_FAILURE");
    return { default: CreateLoadError };
  }));

function CreateLoadError() {
  return <RecoveryNotice title="The Grove Launchpad couldn’t load" reload>
    <Link to="/">Back to terminal</Link>
  </RecoveryNotice>;
}

const BridgePage = lazy(() => import("../pages/BridgePage")
  .then(module => ({ default: module.BridgePage }))
  .catch(() => {
    reportPresentationFailure("bridge", "CHUNK_LOAD_FAILURE");
    return { default: BridgeLoadError };
  }));

function BridgeLoadError() {
  return <RecoveryNotice title="The Grove Crossing couldn’t load" reload>
    <Link to="/">Back to terminal</Link>
  </RecoveryNotice>;
}

const LiquidityPage = lazy(() => import("../pages/LiquidityPage")
  .then(module => ({ default: module.LiquidityPage }))
  .catch(() => {
    reportPresentationFailure("liquidity", "CHUNK_LOAD_FAILURE");
    return { default: LiquidityLoadError };
  }));

function LiquidityLoadError() {
  return <RecoveryNotice title="The liquidity workspace couldn’t load" reload>
    <Link to="/">Back to terminal</Link>
  </RecoveryNotice>;
}

function TasksLoadError() {
  // The failed lazy import is cached. Reload intentionally creates a fresh app;
  // rendering the same lazy instance again would not retry its import.
  return <RecoveryNotice title="The forest trail couldn’t load" reload>
    <Link to="/">Back to terminal</Link>
  </RecoveryNotice>;
}

export function RoutedApp() {
  // Demo is a separate trust domain: never construct live controllers or reads.
  return import.meta.env.VITE_APP_MODE === "DEMO"
    ? <DemoRoutes />
    : <LiveRuntimeProvider><LiveRoutes /></LiveRuntimeProvider>;
}

// Model hooks live above Routes so navigation preserves sessions, mining,
// inventory pages and transaction recovery. Only page presentation unmounts.
function LiveRoutes() {
  useLaunchVerification();
  const location = useLocation();
  return <AppRoutes model={useTerminalAppModel({ terminalActive: location.pathname === "/", tradeActive: matchPath("/trade", location.pathname) !== null })} />;
}
function DemoRoutes() { return <AppRoutes model={useDemoTerminalModel()} />; }

function AppRoutes({ model }: { model: TerminalAppModel }) {
  const location = useLocation();
  const invokeWallet = useWalletNotifications(model.contextKey, model.wallet.error);
  const effects = getPresentationPolicy({ effects: model.effects, mining: model.mining.phase === "MINING" });
  const walletPolicy = location.pathname === "/" || matchPath("/tasks", location.pathname)
    ? "authenticate" : "connect-only";
  const trade = model.trade?.trade;
  const projectToken = model.environment !== "DEMO" && trade && isMerkleV4(trade)
    ? { address: trade.token, symbol: DEX_TOKEN.symbol } : null;

  return (
    <PresentationBoundary feature="page" resetKey={`${location.pathname}:${model.contextKey}`}>
      <Routes>
        <Route element={
          <AppLayout launchpadFees={import.meta.env.VITE_APP_MODE !== "DEMO"} model={model} navigation={<PageNavigation wallet={model.wallet} />} walletPolicy={walletPolicy}
            projectToken={projectToken}
            onWalletAction={invokeWallet}
            onSelectSourceToken={address => invokeWallet(() => model.actions.selectSourceToken(address), "Source token could not be changed.")}>
            {/* The router transitions location updates. Keep this boundary mounted so
                a lazy destination can suspend without hiding the current page. */}
            <Suspense fallback={<main id="main-content" tabIndex={-1}><p role="status">Opening page...</p></main>}>
              <Outlet />
            </Suspense>
          </AppLayout>
        }>
          <Route index element={<TerminalContent model={model} invokeWallet={invokeWallet} />} />
          <Route path="tasks" element={<TasksPage model={model} onWalletAction={invokeWallet}
            effects={effects.effects} setEffects={model.setEffects} />} />
          <Route path="trade" element={<TradePage connection={model.trade} />} />
          {bridgeReviewEnabled() && <Route path="bridge" element={
            <BridgePage {...(model.bridge ? { connection: model.bridge } : {})} demo={import.meta.env.VITE_APP_MODE === "DEMO"} />
          } />}
          {launchpadPagesEnabled && <>
            <Route path="explore" element={<ExplorePage effectsEnabled={effects.animate} />} />
            <Route path="token/:tokenAddress" element={<TokenPage />} />
            <Route path="create" element={<CreatePage />} />
          </>}
          {canAccessLiquidity(model.wallet) && <Route path="liquidity" element={<LiquidityPage />} />}
        </Route>
        <Route path="/x/oauth/callback" element={<XOAuthCallbackPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </PresentationBoundary>
  );
}
